import { Component } from 'vue-property-decorator'
import { mixins } from 'vue-class-component'
import { OptionValidator } from '@/mixins/OptionValidator'
import { ProductApiModel } from '@/interfaces/ProductApiModel'
import lodash from 'lodash'
import { namespace } from 'vuex-class'
import { OptionCategoryApiModel } from '../interfaces/OptionCategoryApiModel'
import { OptionApiModel } from '../interfaces/OptionApiModel'
import { useOptionStore } from '@/store/modules/options'
import { OptionTypeApiModel } from '@/interfaces/OptionTypeApiModel'
import { OptionSelectionMixin } from './OptionSelectionMixin'
import { RequiredOptionsLogic } from '@/interfaces/RequiredOptionsLogic'
const currentModel = namespace('currentModel')
const configurationSizes = namespace('configurationSizes')
const globalValues = namespace('globalValues')

@Component
export class ModelValidator extends mixins(OptionSelectionMixin) {
  @currentModel.State
  public isBeingUpdated!: boolean

  public optionStore = useOptionStore();

  mounted() {
    this.loadModel(this.$route.params.productCode)
      .then(() => this.loadConfigurationSizes(this.model.code))
      .then(() => this.optionStore.loadOptions(this.model.code))

    window.enableValidationDebug = this.enableValidationDebug
  }

  private validating = false

  @currentModel.Action
  public loadModel!: (productCode: string) => Promise<boolean>

  @configurationSizes.Action
  public loadConfigurationSizes!: (productCode: string) => Promise<boolean>

  @globalValues.Action
  public setNotification!: (message?: string) => void

  private logProp = 'countertop';

  public findValidOption(modelToValidate: ProductApiModel, category: OptionCategoryApiModel, currentOption: OptionApiModel | undefined, optionType: OptionTypeApiModel): OptionApiModel | undefined {
    const validOptions = lodash.filter(category?.options, (option) => {
      return (currentOption === undefined || currentOption.code !== option.code) &&
        option.optionType.id === optionType.id &&
        !this.disableOrHideOptionWithModel(option, modelToValidate)
    })

    if (validOptions.length === 0) { return undefined }

    const fallbackDefault = validOptions.find(option => option.isFallbackDefault)
    return fallbackDefault ?? validOptions[0]
  }

  public validateModelProperties(modelToValidate: ProductApiModel, optionType: OptionTypeApiModel):
    Array<{ property: string; value: any, remove: boolean }> {
    const categoryCode = lodash.get(modelToValidate.configurator, optionType.modelProperty + 'Category')

    if (categoryCode !== 'NONE') {
      let propertyValues: any

      if (optionType.multipleSelection) {
        propertyValues = lodash.get(modelToValidate.configurator, optionType.modelProperty)
      } else {
        propertyValues = [lodash.get(modelToValidate.configurator, optionType.modelProperty)]
      }

      if (propertyValues) {
        return propertyValues.flatMap((propertyValue: any) => this.validateModelProperty(optionType, categoryCode, propertyValue, modelToValidate))
      }
    }

    return []
  }

  private validateModelProperty(optionType: OptionTypeApiModel, categoryCode: any, propertyValue: any, modelToValidate: ProductApiModel) {
    const modelProperty = optionType.modelProperty

    const propertiesToChange: Array<{ property: string; value: any, remove: boolean }> = []

    const categories = this.optionStore.menuItems.flatMap(mi => mi.categories).filter(c => c.options.some(o => o.optionType.modelProperty === modelProperty))

    const matchingCats =
      lodash.sortBy(categories.filter(cat => cat.options.some(o => o.code === propertyValue)), cat => cat.code === categoryCode ? 0 : 1)
    const modelCategory = matchingCats[0] as OptionCategoryApiModel

    let modelOption: OptionApiModel | undefined = lodash.find(modelCategory?.options, { code: propertyValue }) as OptionApiModel

    if (this.logProp === modelProperty) {
      console.debug('ModelValidator: ' + modelToValidate.code + ':validate ' + modelProperty, {
        propertyValue,
        categoryCode,
        modelProperty,
        categories,
        modelCategory,
        modelOption
      })
    }

    let subOptionsValidation = this.validateCategorySubOption(modelToValidate, modelCategory)

    if (this.logProp === modelProperty) {
      console.debug('ModelValidator: ' + modelToValidate.code + ':validate sub ' + modelProperty, {
        subOptionsValidation
      })
    }

    if ((modelOption === undefined || (modelOption.restrictions.length > 0 && this.disableOrHideOptionWithModel(modelOption, modelToValidate))) || subOptionsValidation.changeCategory) {
      if (optionType.multipleSelection) {
        if (propertyValue) {
          return [{ property: modelProperty, value: propertyValue, remove: true }]
        }
        return []
      }

      if (optionType.optional) {
        if (propertyValue && propertyValue !== 'NONE') {
          return [{ property: modelProperty, value: 'NONE', remove: false }]
        }
        return []
      }

      modelOption = this.findValidOption(modelToValidate, modelCategory, modelOption, optionType)

      if (this.logProp === modelProperty) {
        console.debug('ModelValidator: ' + modelToValidate.code + ':validate 2 ' + modelProperty, {
          modelOption,
          changeCategory: subOptionsValidation.changeCategory
        })
      }

      if (modelOption === undefined || subOptionsValidation.changeCategory) {
        const newCategory = lodash.find(categories, (category) => {
          subOptionsValidation = this.validateCategorySubOption(modelToValidate, category)
          if (modelCategory?.code === category.code && !subOptionsValidation.changeCategory) {
            return false
          }

          modelOption = this.findValidOption(modelToValidate, category, modelOption, optionType)
          return modelOption !== undefined && !subOptionsValidation.changeCategory
        })
      }

      const updatedCode = modelOption?.code ?? 'NONE'
      if (updatedCode !== propertyValue) { propertiesToChange.push({ property: modelProperty, value: updatedCode, remove: false }) }

      if (this.logProp === modelProperty) {
        console.debug('ModelValidator: ' + modelToValidate.code + ':validate 3 ' + modelProperty, {
          propertiesToChange
        })
      }
    }

    if (modelOption) {
      const description = this.optionTooltipDescription(modelOption)
      const property = modelOption.optionType.modelProperty + '_' + modelOption.code + '_' + 'additionalInformation'
      propertiesToChange.push({ property: property, value: description, remove: description === undefined || description.length === 0 })
    }

    return lodash.concat(propertiesToChange, subOptionsValidation.changes)
  }

  public validateCategorySubOption(modelToValidate: ProductApiModel, category: OptionCategoryApiModel): { changeCategory: boolean; changes: Array<{ property: string; value: any }> } {
    let propertiesToChange: Array<{ property: string; value: any }> = []
    let changeCategory = false

    console.debug('ModelValidator: subVal1', {
      model: modelToValidate?.code,
      cat: category?.code,
      subs: category?.subOptionTypes
    })

    if (category !== undefined && category.subOptionTypes.length > 0) {
      lodash.forEach(category.subOptionTypes, (subOptionType) => {
        const modelSubOptionCode = lodash.get(modelToValidate.configurator, subOptionType.modelProperty)

        const modelSubOption = lodash.find(subOptionType.subOptions, (subOptionItem) => {
          return modelSubOptionCode === subOptionItem.code
        }) as OptionApiModel

        if (subOptionType.code === 'sink') {
          console.debug('ModelValidator: subVal2', {
            model: modelToValidate.code,
            cat: category.code,
            subs: category.subOptionTypes,
            modelSubOption,
            modelSubOptionCode,
            restrictions: modelSubOption?.restrictions
          })
        }

        if (modelSubOption === undefined || (modelSubOption.restrictions.length > 0 && this.disableOrHideOptionWithModel(modelSubOption, modelToValidate))) {
          const newModelSubOption = lodash.find(subOptionType.subOptions, (subOptionItem) => {
            return modelSubOptionCode !== subOptionItem.code && !this.disableOrHideOptionWithModel(subOptionItem, modelToValidate)
          }) as OptionApiModel

          if (subOptionType.modelProperty === 'sink') {
            console.debug('ModelValidator: new', newModelSubOption)
          }

          if (newModelSubOption === undefined) {
            changeCategory = true
          } else {
            propertiesToChange.push({ property: subOptionType.modelProperty, value: newModelSubOption.code })
          }
        }
      })
    }

    if (changeCategory) {
      propertiesToChange = []
    }

    return { changeCategory, changes: propertiesToChange }
  }

  public async validateModel() {
    if (this.validating || this.isBeingUpdated) {
      console.debug('ModelValidator: skip validateModel', { validating: this.validating, isBeingUpdated: this.isBeingUpdated })
      return true
    }

    console.debug('ModelValidator: validateModel', { validating: this.validating, isBeingUpdated: this.isBeingUpdated })

    this.validating = true

    const modelToValidate = lodash.cloneDeep(this.model)

    const propertiesToChange = this.validateAModel(modelToValidate)

    if (propertiesToChange.length > 0) {
      this.setNotification('Design updated with valid options')
      if (this.logProp !== 'nodebug') { console.debug('ModelValidator: validateModel', propertiesToChange) }
      for (const propertyToChange of propertiesToChange) {
        const log = this.logProp === propertyToChange.property
        if (log) {
          console.debug('ModelValidator: current', this.model.configurator[propertyToChange.property])
        }

        if (propertyToChange.remove) {
          const existingOption = this.optionStore.allOptions.find(o => o.optionType.modelProperty === propertyToChange.property &&
            o.code === propertyToChange.value)

          if (existingOption) {
            if (log) {
              console.debug('ModelValidator: Removing existing option ' + existingOption.code)
            }
            await this.deselectOption(existingOption, true)
          }
        } else {
          const newOption = this.optionStore.allOptions.find(o => o.optionType.modelProperty === propertyToChange.property &&
            o.code === propertyToChange.value)

          if (newOption) {
            if (log) {
              console.debug('ModelValidator: Found new option, selecting ' + newOption.code)
            }
            await this.selectOption(newOption, true)
          } else {
            const existingCode = modelToValidate.configurator[propertyToChange.property]

            const existingOption = this.optionStore.allOptions.find(o => o.optionType.modelProperty === propertyToChange.property &&
              o.code === existingCode)

            if (existingOption) {
              if (log) {
                console.debug('ModelValidator: Deselecting existing option ' + existingCode)
              }
              await this.deselectOption(existingOption, true)
            } else {
              if (log) {
                console.debug('ModelValidator: Existing option not found ' + existingCode)
              }
              if (propertyToChange.remove) {
                if (log) {
                  console.debug('ModelValidator: Removing ', propertyToChange)
                }
                this.removeModelPropertyOption(propertyToChange)
              } else {
                if (log) {
                  console.debug('ModelValidator: Updating ', propertyToChange)
                }
                this.updateModelProperty(propertyToChange)
              }
            }
          }
        }
      }
    }

    this.validating = false
    return false
  }

  private validateAModel(modelToValidate: ProductApiModel) {
    const propertiesToChangeAuto = this.optionStore.optionTypes
      .filter(ot => ot.validateOnClient)
      .flatMap(optionType => this.validateModelProperties(modelToValidate, optionType))

    const propertiesToChangeFromRequired = this.optionStore.optionTypes
      .filter(ot => ot.validateOnClient)
      .flatMap(optionType => this.validateRequiredOptions(modelToValidate, optionType, propertiesToChangeAuto.filter(p => p.remove).map(p => p.value)))

    const propertiesToChange: Array<{ property: string; value: any, remove: boolean }> =
      lodash.concat(
        propertiesToChangeAuto,
        propertiesToChangeFromRequired,
        this.validateDepth(modelToValidate))

    return propertiesToChange
  }

  validateRequiredOptions(modelToValidate: ProductApiModel, optionType: OptionTypeApiModel, omit: any[]):
    Array<{ property: string; value: any, remove: boolean }> {
    const propertiesToChange: Array<{ property: string; value: any, remove: boolean }> = []

    const categoryCode = lodash.get(modelToValidate.configurator, optionType.modelProperty + 'Category')

    if (categoryCode !== 'NONE') {
      let propertyValues: any

      if (optionType.multipleSelection) {
        propertyValues = lodash.get(modelToValidate.configurator, optionType.modelProperty)
      } else {
        propertyValues = [lodash.get(modelToValidate.configurator, optionType.modelProperty)]
      }

      if (!propertyValues) {
        return propertiesToChange
      }

      const allSelectedOptions = this.optionStore.allOptions
        .filter(o => o.optionType.modelProperty === optionType.modelProperty &&
          propertyValues.includes(o.code) &&
          !omit.includes(o.code))

      for (const modelOption of allSelectedOptions) {
        if (!modelOption.requiredOptions) {
          continue
        }
        let toSelect = modelOption
          .requiredOptions.map((rCode: string) => this.optionStore.allOptions.find(o => o.code === rCode))
          .filter(r => r)

        const alreadySelected = toSelect.filter(r => this.isOptionSelectedWithModel(r as OptionApiModel, modelToValidate))

        if (toSelect && toSelect.length > 0) {
          if (modelOption.requiredOptionsLogic === RequiredOptionsLogic.Any) {
            if (alreadySelected.length === 0) {
              toSelect = [toSelect[0]]
            } else {
              toSelect = []
            }
          } else {
            toSelect = toSelect.filter(r => !this.isOptionSelectedWithModel(r as OptionApiModel, modelToValidate))
          }

          toSelect.forEach(r => r && propertiesToChange.push({ property: r.optionType.modelProperty, value: r.code, remove: false }))
        }
      }
    }

    return propertiesToChange
  }

  public validateAModelAndUpdate(modelToValidate: ProductApiModel): void {
    const propertiesToChange = this.validateAModel(modelToValidate)

    if (propertiesToChange.length > 0) {
      lodash.forEach(propertiesToChange, (propertyToChange) => {
        if (this.logProp === propertyToChange.property) {
          console.debug('ModelValidator: ' + modelToValidate.code + ' current', this.model.configurator[propertyToChange.property])
          if (propertyToChange.remove) {
            console.debug('ModelValidator: ' + modelToValidate.code + ' removing', propertyToChange)
          } else {
            console.debug('ModelValidator: ' + modelToValidate.code + ' updating', propertyToChange)
          }
        }
        if (propertyToChange.remove) {
          let array = lodash.get(modelToValidate.configurator, propertyToChange.property)
          array = array.filter((a: any) => a !== propertyToChange.value)
          lodash.set(modelToValidate.configurator, propertyToChange.property, array)
        } else { lodash.set(modelToValidate.configurator, propertyToChange.property, propertyToChange.value) }
      })
    }
  }

  validateDepth(modelToValidate: ProductApiModel) {
    const propertiesToChange: Array<{ property: string; value: any, remove: boolean }> = []

    const hasAvailableDepth = modelToValidate.availableDepths.some(d => d === modelToValidate.configurator.depth)
    if (hasAvailableDepth) { return propertiesToChange }

    if (modelToValidate.availableDepths.length === 0) {
      console.warn('No depths configured!')
      return propertiesToChange
    }

    const firstDepth = modelToValidate.availableDepths[0]

    propertiesToChange.push({ property: 'depth', value: firstDepth, remove: false })

    return propertiesToChange
  }

  public enableValidationDebug(prop: string) {
    this.logProp = prop
    return 'OK'
  }
}
